import { Color } from './java/Color';
import { AlphaComposite, Graphics2D } from './java/Graphics2D';

/**
 * Pre-allocated pool for trail particles. Replaces per-trail object allocation
 * with a fixed-size float array — zero GC pressure, O(1) add, single-pass render.
 */
export class TrailPool {

  private static readonly MAX_TRAILS = 512;

  // Per-trail layout: [x, y, width, height, alpha, life, r, g, b, shape]
  private static readonly X = 0;
  private static readonly Y = 1;
  private static readonly W = 2;
  private static readonly H = 3;
  private static readonly ALPHA = 4;
  private static readonly LIFE = 5;
  private static readonly R = 6;
  private static readonly G = 7;
  private static readonly B = 8;
  private static readonly SHAPE = 9;
  private static readonly FIELDS = 10;

  private static readonly pool: number[][] = Array.from(
    { length: TrailPool.MAX_TRAILS },
    () => new Array<number>(TrailPool.FIELDS).fill(0),
  );

  // Pre-cached AlphaComposite instances (0..255)
  private static readonly ALPHA_CACHE: AlphaComposite[] = Array.from(
    { length: 256 },
    (_v, i) => AlphaComposite.getInstance(AlphaComposite.SRC_OVER, i / 255),
  );

  private static readonly OPAQUE: AlphaComposite = TrailPool.ALPHA_CACHE[255];

  /** Was the Java `static { ... }` block. Idempotent; ALPHA_CACHE is already populated above. */
  static init(): void {
    for (let i = 0; i < 256; i++) {
      TrailPool.ALPHA_CACHE[i] = AlphaComposite.getInstance(AlphaComposite.SRC_OVER, i / 255);
    }
  }

  // Memoize opaque trail colors by packed RGB. render() supplies alpha via setComposite,
  // so trails only need a small set of opaque RGBs — this avoids a `new Color` per live
  // trail per frame (byte-identical output). Cache OPAQUE colors only; never alpha-carrying
  // ones, or the composite would double-blend.
  private static readonly COLOR_CACHE: Map<number, Color> = new Map<number, Color>();

  private static opaque(r: number, g: number, b: number): Color {
    const key = (r << 16) | (g << 8) | b;
    let c = TrailPool.COLOR_CACHE.get(key);
    if (c === undefined) {
      c = new Color(r, g, b);
      TrailPool.COLOR_CACHE.set(key, c);
    }
    return c;
  }

  // Shape constants (match Trail.ts)
  static readonly SHAPE_ROUND_RECT = 0;
  static readonly SHAPE_DIAMOND = 1;
  static readonly SHAPE_CIRCLE = 2;
  static readonly SHAPE_TRIANGLE = 3;

  // Scan cursor — start searching for empty slots from here to avoid O(n) worst-case
  private static cursor = 0;

  static add(
    x: number,
    y: number,
    color: Color,
    width: number,
    height: number,
    life: number,
    shape: number = TrailPool.SHAPE_ROUND_RECT,
  ): void {
    for (let j = 0; j < TrailPool.MAX_TRAILS; j++) {
      const i = (TrailPool.cursor + j) % TrailPool.MAX_TRAILS;
      if (TrailPool.pool[i][TrailPool.ALPHA] <= 0) {
        const t = TrailPool.pool[i];
        t[TrailPool.X] = x; t[TrailPool.Y] = y;
        t[TrailPool.W] = width; t[TrailPool.H] = height;
        t[TrailPool.ALPHA] = 1;
        t[TrailPool.LIFE] = life;
        t[TrailPool.R] = color.getRed();
        t[TrailPool.G] = color.getGreen();
        t[TrailPool.B] = color.getBlue();
        t[TrailPool.SHAPE] = shape;
        TrailPool.cursor = (i + 1) % TrailPool.MAX_TRAILS;
        return;
      }
    }
    // Pool full — oldest trail gets overwritten (graceful degradation)
    const t = TrailPool.pool[TrailPool.cursor];
    t[TrailPool.X] = x; t[TrailPool.Y] = y;
    t[TrailPool.W] = width; t[TrailPool.H] = height;
    t[TrailPool.ALPHA] = 1;
    t[TrailPool.LIFE] = life;
    t[TrailPool.R] = color.getRed();
    t[TrailPool.G] = color.getGreen();
    t[TrailPool.B] = color.getBlue();
    t[TrailPool.SHAPE] = shape;
    TrailPool.cursor = (TrailPool.cursor + 1) % TrailPool.MAX_TRAILS;
  }

  static tick(): void {
    for (let i = 0; i < TrailPool.MAX_TRAILS; i++) {
      const t = TrailPool.pool[i];
      if (t[TrailPool.ALPHA] > 0) {
        if (t[TrailPool.ALPHA] > t[TrailPool.LIFE]) {
          t[TrailPool.ALPHA] -= (t[TrailPool.LIFE] - 0.0001);
        } else {
          t[TrailPool.ALPHA] = 0;
        }
      }
    }
  }

  /** Number of currently-alive trails — used for load-aware emission throttling (P1-A). */
  static liveCount(): number {
    let n = 0;
    for (let i = 0; i < TrailPool.MAX_TRAILS; i++) {
      if (TrailPool.pool[i][TrailPool.ALPHA] > 0) n++;
    }
    return n;
  }

  static render(g: Graphics2D): void {
    for (let i = 0; i < TrailPool.MAX_TRAILS; i++) {
      const t = TrailPool.pool[i];
      if (t[TrailPool.ALPHA] <= 0) continue;

      const alphaIdx = Math.min(255, Math.trunc(t[TrailPool.ALPHA] * 255));
      g.setComposite(TrailPool.ALPHA_CACHE[alphaIdx]);
      g.setColor(TrailPool.opaque(
        Math.trunc(t[TrailPool.R]),
        Math.trunc(t[TrailPool.G]),
        Math.trunc(t[TrailPool.B]),
      ));

      const shrink = Math.trunc((1 - t[TrailPool.ALPHA]) * 4);
      const dx = Math.trunc(t[TrailPool.X]) + shrink;
      const dy = Math.trunc(t[TrailPool.Y]) + shrink;
      const dw = Math.trunc(t[TrailPool.W]) - shrink * 2;
      const dh = Math.trunc(t[TrailPool.H]) - shrink * 2;

      if (dw <= 0 || dh <= 0) continue;

      const cx = dx + Math.trunc(dw / 2);
      const cy = dy + Math.trunc(dh / 2);
      const half = Math.trunc(dw / 2);

      switch (Math.trunc(t[TrailPool.SHAPE])) {
        case TrailPool.SHAPE_DIAMOND:
          g.fillPolygon(
            [cx, cx + half, cx, cx - half],
            [cy - half, cy, cy + half, cy], 4);
          break;
        case TrailPool.SHAPE_CIRCLE:
          g.fillOval(dx, dy, dw, dh);
          break;
        case TrailPool.SHAPE_TRIANGLE:
          g.fillPolygon(
            [cx, cx + half, cx - half],
            [cy - half, cy + half, cy + half], 3);
          break;
        default:
          g.fillRoundRect(dx, dy, dw, dh, 8, 8);
          break;
      }
    }
    g.setComposite(TrailPool.OPAQUE);
  }

  static clear(): void {
    for (let i = 0; i < TrailPool.MAX_TRAILS; i++) {
      TrailPool.pool[i][TrailPool.ALPHA] = 0;
    }
    TrailPool.cursor = 0;
  }
}
